<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.gamification_sets', 2),
                        href: meta.links.set_index,
                    },
                    {
                        name: $t('messages.usage_information'),
                        href: null,
                    },
                ]"
            />
        </template>

        <div class="flex flex-wrap gap-x-2">
            <div>
                <div>
                    {{ $tc('messages.gamification_sets', 2) }}
                </div>
                <NavLinks :links="meta.navLinks.setNavLinks" />
            </div>
            <div>
                <div>
                    {{ $t('messages.sort_order') }}
                </div>
                <NavLinks :links="meta.navLinks.orderNavLinks" />
            </div>
        </div>

        <div class="flex gap-x-2" v-if="meta.navLinks.groupNavLinks">
            <div>
                <div>
                    {{ $tc('messages.groups', 2) }}
                </div>
                <NavLinks :links="meta.navLinks.groupNavLinks" />
            </div>
        </div>

        <form :action="meta.links.index" method="post" @submit.prevent="filter">
            <div class="grid grid-cols-2 sm:grid-cols-12 sm:gap-3 md:gap-6">
                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.date_from')" for="date_from" />
                        <FormInput id="date_from" v-model="form.date_from" type="date" name="date_from" class="mt-1" />
                        <FormError :value="form.errors.date_from" class="mt-2" />
                    </div>
                </div>
                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.date_to')" for="date_to" />
                        <FormInput id="date_to" v-model="form.date_to" type="date" name="date_to" class="mt-1" />
                        <FormError :value="form.errors.date_to" class="mt-2" />
                    </div>
                </div>
                <div class="sm:col-span-2 align-bottom">
                    <label class="block text-sm font-medium leading-5 text-gray-700 mt-1"> &nbsp; </label>
                    <div class="mt-1">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <div>
            <apexchart
                :type="charts.points.type"
                :height="charts.points.chart.height"
                :series="charts.points.series"
                :options="charts.points"
            >
            </apexchart>
        </div>

        <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2 md:grid-cols-2">
            <div class="relative col-span-1 flex shadow rounded-md" v-for="teacher in teachers" :key="teacher.id">
                <div
                    class="shrink-0 flex items-center justify-center w-24 text-white text-sm leading-5 font-medium rounded-l-md overflow-hidden"
                >
                    <InertiaLink :href="teacher.links.show">
                        <img :src="teacher.avatar_or_default" alt="profile photo" />
                    </InertiaLink>
                </div>
                <div
                    class="flex-1 flex items-center justify-between border-t border-r border-b border-gray-200 bg-white rounded-r-md truncate"
                >
                    <div class="flex-1 px-4 py-2 text-sm leading-5 truncate">
                        <div>
                            <InertiaLink
                                :href="teacher.links.show"
                                class="text-gray-900 font-medium hover:text-gray-600 transition ease-in-out duration-150"
                            >
                                {{ teacher.full_name }}
                            </InertiaLink>
                            <span v-if="teacher.group_name"> ({{ teacher.group_name }}) </span>
                        </div>

                        <div class="flex space-x-2 items-center">
                            <div>
                                <InertiaSquareLinkButton action="show" :href="teacher.links.usage_show" />
                            </div>
                            <div>
                                <InertiaLink href="/">
                                    <Tippy>
                                        <IconDeviceDesktopAnalytics class="mr-1 text-indigo-500 h-4 w-4" />

                                        <template #content>
                                            <ul class="text-sm p-1" v-if="teacher.actions.length">
                                                <li v-for="action in teacher.actions" :key="action.key">
                                                    {{ action.text }} : {{ action.count }}
                                                </li>
                                            </ul>
                                            <span v-else> <Icon3dCubeSphereOff class="text-white h-6 w-6" /> </span>
                                        </template>
                                    </Tippy>
                                </InertiaLink>
                            </div>
                            <div class="flex flex-wrap space-x-1 justify-between">
                                <div>
                                    <BadgePill color="green" v-if="teacher.points_total_display">
                                        {{ teacher.points_total_display }}
                                    </BadgePill>
                                </div>

                                <div>
                                    <BadgePill color="yellow" v-if="teacher.coins_points_positive_total_display">
                                        {{ teacher.coins_points_positive_total_display }}
                                    </BadgePill>
                                </div>

                                <div>
                                    <BadgePill color="yellow" v-if="teacher.coins_points_negative_total_display">
                                        {{ teacher.coins_points_negative_total_display }}
                                    </BadgePill>
                                </div>

                                <div>
                                    <BadgePill color="red" v-if="teacher.health_points_positive_total_display">
                                        {{ teacher.health_points_positive_total_display }}
                                    </BadgePill>
                                </div>

                                <div>
                                    <BadgePill color="red" v-if="teacher.health_points_negative_total_display">
                                        {{ teacher.health_points_negative_total_display }}
                                    </BadgePill>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import BadgePill from '@/components/BadgePill.vue';
import NavLinks from '@/components/NavLinks.vue';
import { Icon3dCubeSphereOff, IconDeviceDesktopAnalytics } from '@tabler/icons-vue';
import { Tippy } from 'vue-tippy';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormError from '@/components/Forms/FormError.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        FormError,
        FormInput,
        FormLabel,
        InertiaSquareLinkButton,
        NavLinks,
        BadgePill,
        PanelPage,
        Tippy,
        IconDeviceDesktopAnalytics,
        Icon3dCubeSphereOff,
    },
    layout: Layout,
    name: 'Index',
    props: ['meta', 'teachers', 'filters', 'charts'],
    data() {
        return {
            form: this.$inertia.form({
                sort: this.filters.sort,
                group_id: this.filters.group_id,
                set_id: this.filters.set_id,
                date_from: this.filters.date_from,
                date_to: this.filters.date_to,
            }),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index, {
                preserveScroll: true,
            });
        },
    },
};
</script>
